import {Link, useParams} from 'react-router-dom';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Footer} from '@common/ui/footer/footer';
import {Button} from '@common/ui/buttons/button';
import {Trans} from '@common/i18n/trans';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {useSettings} from '@common/core/settings/use-settings';
import {LaunchOfferBanner} from '@app/landing/launch-offer-banner';
import {useLandingPageStats} from '@app/landing/use-landing-page-stats';
import {PageSeo} from '@app/landing/page-seo';
import {
  GET_STARTED_STEPS,
  getCommonMistakes,
  getExamples,
  getFreeFaqAnswer,
} from '@app/landing/generic-page-sections';
import {
  getAllTopics,
  getGroupLabel,
  getTopic,
  TopicGroup,
} from './topic-page-data';
import '../landing-page.css';

const PRO_TIPS = (name: string) => [
  {
    title: 'Prioritize your top goal',
    body: `Decide the one action you want most visitors to take and put that link first. Most people using ${name} lose conversions by giving every link equal weight.`,
  },
  {
    title: 'Use the right link type',
    body: `Don't just list links. Use custom aliases, password-protected links for private content, and expiring links for limited-time offers.`,
  },
  {
    title: 'Review analytics weekly',
    body: `Check which links people actually click, not just how many people visit your page. A weekly look at click analytics tells you what to feature higher next.`,
  },
];

function getFaq(
  name: string,
  siteName: string,
  feature1: string,
  feature2: string,
  isLaunchActive: boolean,
) {
  return [
    {
      q: `Is ${siteName} free for ${name}?`,
      a: getFreeFaqAnswer(siteName, isLaunchActive),
    },
    {
      q: `What should I use ${name} for on my bio page?`,
      a: `Most people get the best results from a mix of a branded bio page, a way to handle ${feature1.toLowerCase()}, and a trackable link for ${feature2.toLowerCase()}, rather than a single generic link.`,
    },
    {
      q: `How often should I update my link in bio?`,
      a: `Review your bio page at least weekly. Swap out expired offers, refresh top links based on click analytics, and remove anything that's no longer relevant to keep conversion rates high.`,
    },
    {
      q: `Is ${siteName} better than other link-in-bio tools for ${name}?`,
      a: `${siteName} includes custom domains, UTM tracking, retargeting pixels, password-protected and expiring links, and free QR codes on every account, features many link-in-bio tools reserve for paid plans or don't offer at all.`,
    },
  ];
}

interface Props {
  group: TopicGroup;
}
export function TopicPage({group}: Props) {
  const {slug, sub} = useParams();
  const {branding, base_url} = useSettings();
  const topic = getTopic(group, slug, sub);
  const {data: statsData} = useLandingPageStats();
  const isLaunchActive = !!statsData?.stats.launchOffer.active;

  if (!topic) {
    return <NotFoundPage />;
  }

  const siteName = branding?.site_name || 'LinkInBio';
  const related = getAllTopics(group)
    .filter(t => t.slug !== topic.slug)
    .slice(0, 4);
  const [f1, f2] = topic.features;
  const faq = getFaq(
    topic.name,
    siteName,
    f1?.title || 'links',
    f2?.title || 'analytics',
    isLaunchActive,
  );
  const examples = getExamples(f1?.title || 'links', f2?.title || 'analytics');
  const mistakes = getCommonMistakes(topic.name);
  const pageUrl = `${(base_url || '').replace(/\/$/, '')}/${group}/${topic.slug}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSeo
        title={topic.metaTitle}
        description={topic.metaDescription}
        url={pageUrl}
      />
      <Navbar
        color="bg"
        border="border-b"
        className="lp-sticky-nav"
        menuPosition="homepage-navbar"
      />

      <main className="flex-auto">
        {/* HERO */}
        <header className="pt-80 md:pt-100 pb-40">
          <div className="landing-container max-w-[900px] text-center">
            <LaunchOfferBanner className="mb-24 inline-block" />
            <span className="section-label">
              {getGroupLabel(group).toUpperCase()}
            </span>
            <h1 className="alt-heading mb-16">
              {siteName} for {topic.name}
            </h1>
            <p className="text-base text-muted mb-32 max-w-[700px] mx-auto">
              {topic.metaDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-16">
              <Button
                elementType={Link}
                to="/register"
                variant="flat"
                color="primary"
                size="md"
                radius="rounded-full"
              >
                Get started free
              </Button>
              <Button
                elementType={Link}
                to="/pricing"
                variant="outline"
                color="primary"
                size="md"
                radius="rounded-full"
              >
                See pricing
              </Button>
            </div>
          </div>
        </header>

        {/* WHY IT MATTERS */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[800px]">
            <span className="section-label">WHY IT MATTERS</span>
            <h2 className="alt-heading mb-24 text-2xl md:text-3xl">
              Why {topic.name} needs the right link in bio
            </h2>
            {topic.whyItMatters.map((p, i) => (
              <p key={i} className="text-base text-muted mb-16">
                {p}
              </p>
            ))}
          </div>
        </section>

        {/* PRO TIPS */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[1000px]">
            <span className="section-label">PRO TIPS</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Tips for {topic.name}
            </h2>
            <div className="grid md:grid-cols-3 gap-24">
              {PRO_TIPS(topic.name).map((tip, i) => (
                <div key={i} className="rounded-xl border p-20">
                  <div className="text-2xl font-extrabold text-primary/40 mb-8">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-semibold mb-8">{tip.title}</h3>
                  <p className="text-sm text-muted">{tip.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* KEY FEATURES */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[1000px]">
            <span className="section-label">KEY FEATURES</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Everything you need for {topic.name}
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              {topic.features.map((feature, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{feature.title}</h3>
                  <p className="text-sm text-muted">{feature.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GET STARTED */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[1000px]">
            <span className="section-label">GET STARTED</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Up and running in 3 steps
            </h2>
            <div className="grid md:grid-cols-3 gap-24">
              {GET_STARTED_STEPS.map((step, i) => (
                <div key={i} className="rounded-xl border p-20">
                  <div className="text-2xl font-extrabold text-primary/40 mb-8">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-semibold mb-8">{step.title}</h3>
                  <p className="text-sm text-muted">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[900px]">
            <span className="section-label">EXAMPLES</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              What a great {topic.name} page looks like
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              <div className="rounded-xl border bg-white p-24">
                <div className="text-xs font-bold uppercase tracking-wide text-muted mb-12">
                  Just starting out
                </div>
                <ul className="text-sm text-muted flex flex-col gap-8">
                  {examples.starting.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-primary/30 bg-white p-24">
                <div className="text-xs font-bold uppercase tracking-wide text-primary mb-12">
                  Established {topic.name}
                </div>
                <ul className="text-sm flex flex-col gap-8">
                  {examples.established.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[900px]">
            <span className="section-label">WHY {siteName.toUpperCase()}</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              More than a list of links
            </h2>
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-sm border-collapse min-w-[500px]">
                <thead>
                  <tr className="bg-alt text-left">
                    <th className="p-16 font-semibold">Feature</th>
                    <th className="p-16 font-semibold text-muted">
                      Other tools
                    </th>
                    <th className="p-16 font-semibold text-primary">
                      {siteName}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t align-top">
                    <td className="p-16 font-medium">Custom domain</td>
                    <td className="p-16 text-muted">
                      Often paywalled or unavailable
                    </td>
                    <td className="p-16">Included</td>
                  </tr>
                  <tr className="border-t align-top">
                    <td className="p-16 font-medium">Link tracking</td>
                    <td className="p-16 text-muted">
                      Basic click counts, if any
                    </td>
                    <td className="p-16">
                      UTM tracking, retargeting pixels, per-link analytics
                    </td>
                  </tr>
                  <tr className="border-t align-top">
                    <td className="p-16 font-medium">
                      Password &amp; expiring links
                    </td>
                    <td className="p-16 text-muted">Rarely offered</td>
                    <td className="p-16">Included on every link</td>
                  </tr>
                  <tr className="border-t align-top">
                    <td className="p-16 font-medium">QR codes</td>
                    <td className="p-16 text-muted">
                      Often a separate paid tool
                    </td>
                    <td className="p-16">Free on every link</td>
                  </tr>
                  <tr className="border-t align-top">
                    <td className="p-16 font-medium">Built for {topic.name}</td>
                    <td className="p-16 text-muted">
                      Generic, one-size-fits-all
                    </td>
                    <td className="p-16">
                      Designed around how {topic.name} actually works
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ANALYTICS */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[800px]">
            <span className="section-label">ANALYTICS</span>
            <h2 className="alt-heading mb-24 text-2xl md:text-3xl">
              Know exactly what works
            </h2>
            <p className="text-base text-muted">
              Every {siteName} page includes click analytics, tracking pixel
              integration, and per-link UTM tracking, so you can see which
              link actually gets clicked, not just how many people visited
              the page.
            </p>
          </div>
        </section>

        {/* COMMON MISTAKES */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[1000px]">
            <span className="section-label">AVOID THESE MISTAKES</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Common mistakes to avoid
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              {mistakes.map((mistake, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{mistake.title}</h3>
                  <p className="text-sm text-muted">{mistake.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[800px]">
            <span className="section-label">WHO IT'S FOR</span>
            <h2 className="alt-heading mb-24 text-2xl md:text-3xl">
              Is {siteName} right for {topic.name}?
            </h2>
            <p className="text-base text-muted">
              {siteName} is built for {topic.name} who want a branded bio
              page with real link controls, not just a static list of URLs.
              If you're not sure this is the right fit,{' '}
              <Link to={`/${group}`} className="text-primary font-medium">
                browse other {getGroupLabel(group).toLowerCase()}
              </Link>{' '}
              to find a closer match.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[800px]">
            <span className="section-label">FAQ</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Frequently asked questions
            </h2>
            <div className="flex flex-col gap-20">
              {faq.map((item, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{item.q}</h3>
                  <p className="text-sm text-muted">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-80 md:pb-100 text-center bg-alt">
          <div className="landing-container max-w-[700px]">
            <h2 className="alt-heading mb-16 text-2xl md:text-3xl">
              Your {topic.name} page is one link away
            </h2>
            <Button
              elementType={Link}
              to="/register"
              variant="flat"
              color="primary"
              size="md"
              radius="rounded-full"
              className="mt-16"
            >
              <Trans message="Get started free" />
            </Button>
          </div>
        </section>

        {/* RELATED */}
        {related.length > 0 && (
          <section className="pb-80 md:pb-100">
            <div className="landing-container max-w-[1000px]">
              <h2 className="alt-heading mb-24 text-xl">
                More {getGroupLabel(group)}
              </h2>
              <div className="flex flex-wrap gap-12">
                {related.map(t => (
                  <Link
                    key={t.slug}
                    to={`/${group}/${t.slug}`}
                    className="rounded-full border px-20 py-10 text-sm font-medium hover:bg-hover"
                  >
                    {t.name}
                  </Link>
                ))}
                <Link
                  to={`/${group}`}
                  className="rounded-full border px-20 py-10 text-sm font-medium hover:bg-hover"
                >
                  See all {getGroupLabel(group).toLowerCase()}
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer className="mx-14 md:mx-40" />
    </div>
  );
}
